/**
 * Account Complete Page
 * AD-20251113-STRIPE-ONBOARDING: Post-checkout success + account onboarding
 * AD-20251115-SIGNUP-SESSION-FIX: Fixed signup session establishment
 * AD-202512XX-PASSWORD-FIRST: Password-first flow - no magic links
 *
 * Shown after successful Stripe checkout. Verifies payment, shows success UI with confetti,
 * and guides users through password-based account creation to claim their purchases.
 * 
 * Flow:
 * 1. User enters password on success page
 * 2. Server creates/updates user with password (gated by Stripe session email match)
 * 3. Client signs in with password
 * 4. Claims purchases and redirects to /account
 * 
 * Email verification is handled in account area, not blocking here.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface CheckoutLookupResponse {
  ok: boolean;
  sessionId?: string;
  email?: string;
  purchases?: Array<{
    domain: string;
    planType: 'BIN' | 'LTO_12' | 'LTO_24' | 'LTO_36';
    isSubscription: boolean;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    stripePaymentIntentId: string | null;
  }>;
  code?: string;
  message?: string;
}

interface ClaimPurchasesResponse {
  ok: boolean;
  claimedCount?: number;
  domains?: string[];
  message?: string;
}

export default function AccountComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, session } = useAuth();
  const [checkoutData, setCheckoutData] = useState<CheckoutLookupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const confettiTriggered = useRef(false);
  const isCheckoutDevMode = import.meta.env.VITE_CHECKOUT_DEV_MODE === 'true';

  // Get session_id from URL
  const urlSessionId = searchParams.get('session_id');
  const sessionId = urlSessionId;

  const validatePasswordComplexity = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must include at least one number (per Supabase auth guidelines).';
    }
    if (!/[a-zA-Z]/.test(pwd)) {
      return 'Password must include at least one letter (per Supabase auth guidelines).';
    }
    return null;
  };

  const validateEmail = (email: string): string | null => {
    if (!email || !email.trim()) {
      return 'Email is required';
    }
    // Basic email validation - Supabase will do stricter validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    // Check for common issues
    const trimmed = email.trim();
    if (trimmed.length > 254) {
      return 'Email address is too long';
    }
    if (trimmed.includes('..') || trimmed.startsWith('.') || trimmed.endsWith('.')) {
      return 'Email address format is invalid';
    }
    return null;
  };

  // Trigger confetti animation on success
  useEffect(() => {
    if (checkoutData?.ok && !confettiTriggered.current) {
      confettiTriggered.current = true;
      // Simple confetti effect using CSS animation
      // For a more elaborate effect, consider adding canvas-confetti library
      const confetti = document.createElement('div');
      confetti.style.position = 'fixed';
      confetti.style.top = '0';
      confetti.style.left = '0';
      confetti.style.width = '100%';
      confetti.style.height = '100%';
      confetti.style.pointerEvents = 'none';
      confetti.style.zIndex = '9999';
      confetti.innerHTML = Array.from({ length: 50 }, (_, i) => {
        const delay = Math.random() * 2;
        const duration = 2 + Math.random() * 2;
        const left = Math.random() * 100;
        const colors = ['#0000FF', '#4D7DFF', '#FFD700', '#FF6B6B', '#51CF66'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        return `<div style="position: absolute; left: ${left}%; top: -10px; width: 10px; height: 10px; background: ${color}; border-radius: 50%; animation: confetti-fall ${duration}s ${delay}s linear forwards;"></div>`;
      }).join('');
      document.body.appendChild(confetti);
      
      // Add CSS animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes confetti-fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);

      setTimeout(() => {
        confetti.remove();
        style.remove();
      }, 5000);
    }
  }, [checkoutData]);

  // Fetch checkout session data on mount
  useEffect(() => {
    if (!sessionId) {
      setError('Missing session ID. Please return to checkout or contact support.');
      setLoading(false);
      return;
    }

    if (!sessionId.startsWith('cs_')) {
      setError('Invalid session ID format.');
      setLoading(false);
      return;
    }

    const fetchCheckoutData = async () => {
      try {
        // Dev mode: Allow testing with mock data for test session IDs
        const isDevMode = import.meta.env.DEV;
        const isTestSession = sessionId.startsWith('cs_test_') && (sessionId === 'cs_test_example' || sessionId.length < 20);
        
        if (isDevMode && isTestSession) {
          // Use mock data for testing
          const mockData: CheckoutLookupResponse = {
            ok: true,
            sessionId: sessionId,
            email: 'test@example.com',
            purchases: [{
              domain: 'test-domain.com',
              planType: 'BIN',
              isSubscription: false,
              stripeCustomerId: null,
              stripeSubscriptionId: null,
              stripePaymentIntentId: null,
            }],
          };
          setCheckoutData(mockData);
          setLoading(false);
          return;
        }

        const response = await fetch(`/api/manage?action=checkout.lookup&session_id=${encodeURIComponent(sessionId)}`);
        const data: CheckoutLookupResponse = await response.json();
        
        if (!data.ok) {
          if (data.code === 'INVALID_SESSION' || data.code === 'NOT_FOUND') {
            setError('We couldn\'t verify your payment. Please contact support with your receipt.');
          } else if (data.code === 'UNPAID') {
            setError('Payment is still processing. Please wait a moment and refresh the page.');
          } else {
            setError(data.message || 'Something went wrong. Please contact support.');
          }
        } else {
          setCheckoutData(data);
        }
      } catch (err) {
        setError('Failed to verify your purchase. Please contact support.');
        console.error('[AccountComplete] Checkout lookup error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
  }, [sessionId]);

  // Auto-claim purchases if user is logged in with matching email
  useEffect(() => {
    if (!authLoading && user && session && checkoutData?.ok && !claiming && !claimError) {
      const stripeEmail = checkoutData.email?.toLowerCase().trim();
      const userEmail = user.email?.toLowerCase().trim();

      if (stripeEmail && userEmail && stripeEmail === userEmail) {
        // Emails match - auto-claim
        claimPurchases(stripeEmail);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, session, checkoutData, claiming, claimError]);

  const claimPurchases = async (email: string) => {
    // Get current session (may have changed after signup/login)
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession?.access_token) {
      setClaimError('Authentication required. Please sign in again.');
      return;
    }

    setClaiming(true);
    setClaimError(null);

    try {
      const response = await fetch('/api/manage?action=account.claimPurchases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ email }),
      });

      const data: ClaimPurchasesResponse = await response.json();

      if (!data.ok) {
        setClaimError(data.message || 'Failed to claim purchases');
        setClaiming(false);
      } else {
        // Success - redirect to account page with welcome context
        navigate('/account?welcome=true', { replace: true });
      }
    } catch (err) {
      setClaimError('Failed to claim purchases. Please contact support.');
      console.error('[AccountComplete] Claim purchases error:', err);
      setClaiming(false);
    }
  };

  /**
   * AD-20251116-AUTH-Redirect-After-Create: Post-purchase account creation handler
   * 
   * Flow:
   * 1. signUp: Create Supabase user with Stripe email + password
   * 2. signInWithPassword: Attempt to establish session (may fail if email not confirmed)
   * 3. On success: claimPurchases → redirect to /account?welcome=true
   * 4. On "email not confirmed": Redirect to /account?verify_pending=1&welcome=true&email=...
   *    (instead of staying on success page with dead-end message)
   * 
   * This ensures users land in the account experience with verification banner,
   * not stuck on the success page.
   */
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setSubmitting(true);

    // Always use the email from Stripe checkout - it must match to link the purchase
    const emailToUse = checkoutData?.email;
    
    if (!emailToUse) {
      setAuthError('Email is required. Please contact support if this error persists.');
      setSubmitting(false);
      return;
    }
    
    // Validate email format before submitting
    const emailError = validateEmail(emailToUse);
    if (emailError) {
      setAuthError(emailError);
      setSubmitting(false);
      return;
    }
    
    const trimmedEmail = emailToUse.trim();

    if (!sessionId) {
      setAuthError('Session ID is required');
      setSubmitting(false);
      return;
    }

    const complexityError = validatePasswordComplexity(password);
    if (complexityError) {
      setAuthError(complexityError);
      setSubmitting(false);
      return;
    }

    try {
      // Step 1: Create the account client-side with Supabase Auth
      // Use explicit emailRedirectTo so verification links land on /account/email-confirmed
      const emailRedirectTo =
        import.meta.env.DEV
          ? 'http://localhost:8081/account/email-confirmed'
          : 'https://attaindomains.com/account/email-confirmed';

      const { error: signUpError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo,
        },
      });

      if (signUpError) {
        // Log the full error for debugging
        console.error('[AccountComplete] Supabase signUp error:', {
          message: signUpError.message,
          status: signUpError.status,
          name: signUpError.name,
          email: trimmedEmail,
        });
        
        const errorMsg = signUpError.message?.toLowerCase?.() || '';
        if (errorMsg.includes('already registered') || errorMsg.includes('already exists') || errorMsg.includes('user already registered')) {
          // Account already exists - show friendly message with login CTA
          setAuthError('Looks like you already have an account with this email. Log in instead.');
          setSubmitting(false);
          return;
        } else if (errorMsg.includes('password')) {
          setAuthError(signUpError.message || 'Password is too weak. Please use a stronger password.');
        } else if (errorMsg.includes('invalid') && errorMsg.includes('email')) {
          // Supabase email validation error - show the actual error message
          setAuthError(signUpError.message || `Email address "${trimmedEmail}" is invalid. Please check the email address and try again.`);
        } else {
          // Show Supabase's error message directly
          setAuthError(signUpError.message || 'Failed to create account. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      // Step 2: Try to sign in with password (if email confirmation is optional)
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (signInError) {
        const errorMsg = signInError.message.toLowerCase();
        if (errorMsg.includes('email not confirmed')) {
          // Account created but email not confirmed - redirect to /account with verify-pending context
          // This avoids the dead-end "check your email" message on the success page
          navigate(`/account?verify_pending=1&welcome=true&email=${encodeURIComponent(trimmedEmail)}`, { replace: true });
          return;
        } else if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid password')) {
          setAuthError('Invalid email or password. Please try again.');
        } else if (
          errorMsg.includes('not configured') ||
          errorMsg.includes('configuration') ||
          errorMsg.includes('invalid api key') ||
          errorMsg.includes('network') ||
          errorMsg.includes('fetch')
        ) {
          setAuthError('Unable to connect to authentication service. Please check your internet connection and try again.');
        } else {
          setAuthError(signInError.message || 'Sign in failed. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      if (signInData.user && signInData.session) {
        // Step 3: Claim purchases and redirect (with welcome context)
        await claimPurchases(trimmedEmail);
      } else {
        // Edge case: signup succeeded but no session/user returned
        // Redirect to verify-pending state
        navigate(`/account?verify_pending=1&welcome=true&email=${encodeURIComponent(trimmedEmail)}`, { replace: true });
      }
    } catch (err) {
      // Network or unexpected errors - show generic message with support link
      setAuthError('Failed to create account. Please try again or contact support if this persists.');
      console.error('[AccountComplete] Create account error:', err);
      setSubmitting(false);
    }
  };


  const handleLogout = async () => {
    await supabase.auth.signOut();
    // Page will re-render and show guest onboarding
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  // Error state (missing/invalid session_id or lookup failed)
  if (error || !checkoutData?.ok) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader>
              <div className="flex justify-center mb-4">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <CardTitle className="text-2xl text-center">Something went wrong</CardTitle>
              <CardDescription className="text-center">
                {error || checkoutData?.message || 'We couldn\'t verify your payment.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  If you completed a purchase, please contact support with your receipt or the last 4 digits of your card and purchase date.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
                  Go Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/support')} className="flex-1">
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state - determine which UI to show
  const stripeEmail = checkoutData.email?.toLowerCase().trim();
  const userEmail = user?.email?.toLowerCase().trim();
  const emailsMatch = stripeEmail && userEmail && stripeEmail === userEmail;

  // Case A: Logged in with matching email - auto-claiming
  if (user && session && emailsMatch) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-xl mx-auto">
          {/* Success Hero */}
          <Card className="p-8">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <CardTitle className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
                Success! You just secured {checkoutData.purchases?.[0]?.domain || 'your domain'} 🎉
              </CardTitle>
              <CardDescription className="text-lg">
                {checkoutData.purchases?.[0]?.planType === 'BIN' 
                  ? 'Your Buy It Now purchase is complete.'
                  : `Your Lease-to-Own plan for ${checkoutData.purchases?.[0]?.planType === 'LTO_24' ? '24' : checkoutData.purchases?.[0]?.planType === 'LTO_36' ? '36' : '12'} months is active.`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {claiming ? (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <p className="text-sm text-muted-foreground">Finishing up...</p>
                </div>
              ) : claimError ? (
                <Alert variant="destructive">
                  <AlertDescription>{claimError}</AlertDescription>
                </Alert>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    We've added {checkoutData.purchases?.[0]?.domain || 'your domain'} to your Attain Domains account.
                  </p>
                  <Button onClick={() => navigate('/account', { replace: true })} size="lg" className="w-full">
                    Go to your domains
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Case B: Logged in but emails don't match
  if (user && session && !emailsMatch) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-xl mx-auto">
          {/* Success Hero */}
          <Card className="p-8">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <CardTitle className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
                Success! You just secured {checkoutData.purchases?.[0]?.domain || 'your domain'} 🎉
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You're currently logged in as <strong>{user.email}</strong>, but this purchase is for <strong>{checkoutData.email}</strong>. 
                  Log out and sign in or create an account for {checkoutData.email} to claim this domain.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleLogout} className="flex-1">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out and switch account
                </Button>
                <Button variant="outline" onClick={() => navigate('/support')} className="flex-1">
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Case C: Guest buyer - show onboarding
  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-4xl mx-auto">
        {/* Single Success Card */}
        <Card className="p-8">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Your purchase was successful!
            </CardTitle>
            <CardDescription className="text-2xl md:text-3xl">
              Create your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isCheckoutDevMode && (
              <Alert className="mb-4">
                <AlertDescription className="text-xs text-muted-foreground">
                  Checkout dev mode is enabled locally. Stripe verification is bypassed using the official
                  testing helpers (
                  <a
                    href="https://stripe.com/docs/testing#checkout"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    docs
                  </a>
                  ). Disable <code>CHECKOUT_DEV_MODE</code> before deploying to production.
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-create" className="text-base">Email</Label>
                <Input
                  id="email-create"
                  type="email"
                  value={checkoutData.email || ''}
                  readOnly
                  className="bg-muted cursor-not-allowed"
                  placeholder="Your email address"
                  title="Email from your Stripe purchase (cannot be changed)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password-create" className="text-base">Password</Label>
                <div className="relative">
                  <Input
                    id="password-create"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {authError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {authError}
                    {authError.includes('already have an account') && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/account/login?redirect=/account')}
                          className="w-full"
                        >
                          Log in instead
                        </Button>
                      </div>
                    )}
                    {authError.includes('contact support') && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/support')}
                          className="w-full"
                        >
                          Contact Support
                        </Button>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </form>

            {claiming && (
              <div className="mt-4 flex items-center justify-center gap-2 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <p className="text-sm text-muted-foreground">Linking your purchase...</p>
              </div>
            )}

            {claimError && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{claimError}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
